import { ChangeEvent, FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faSearch } from "@fortawesome/pro-regular-svg-icons";

import { Modal } from "@/components/Modals/Modal";
import { SearchField } from "@/components/SearchField/SearchField";
import { Spinner } from "@/components/Spinner/Spinner";
import { Icon } from "@/components/Icon/Icon";
import { useGetCampaignsForCopyQuery } from "@/pages/Private/redux/list/list.api";

import { Campaign } from "../../schema/campaign";

import { ListGroupRow } from "./ListGroupRow";
import { CampaignListGroup } from "./types";
import {
	filterCampaignGroups,
	groupCampaignsByList,
	isGroupFullySelected,
	isGroupPartiallySelected,
} from "./utils";

// All copyable campaigns are fetched up front so search filters the in-memory
// result. Campaigns live inside lists, so the count stays small — no paging.
const COPY_CAMPAIGNS_FETCH_LIMIT = 1000;

interface CopyCampaignsSelectorProps {
	isOpened: boolean;
	handleClose: () => void;
	handleSelect: (campaigns: Campaign[]) => void;
	listId: number;
}

export const CopyCampaignsSelector: FunctionComponent<CopyCampaignsSelectorProps> = ({
	isOpened,
	handleClose,
	handleSelect,
	listId,
}) => {
	const { t } = useTranslation("translation", { keyPrefix: "list" });

	// The modal is only mounted while open, so `refetchOnMountOrArgChange`
	// reloads the available campaigns on every open.
	const { data, isFetching } = useGetCampaignsForCopyQuery(
		{ id: listId, page: 1, limit: COPY_CAMPAIGNS_FETCH_LIMIT },
		{ refetchOnMountOrArgChange: true }
	);

	const [searchValue, setSearchValue] = useState("");
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [expandedListIds, setExpandedListIds] = useState<Set<number>>(new Set());

	const campaigns = useMemo(() => data?.data ?? [], [data?.data]);
	const groups = useMemo(() => groupCampaignsByList(campaigns), [campaigns]);
	const visibleGroups = useMemo(
		() => filterCampaignGroups(groups, searchValue),
		[groups, searchValue]
	);

	const isSearching = searchValue.trim().length > 0;

	const toggleCampaign = (campaignId: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);

			if (next.has(campaignId)) {
				next.delete(campaignId);
			} else {
				next.add(campaignId);
			}

			return next;
		});
	};

	const toggleList = (group: CampaignListGroup) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			const fullySelected = group.campaigns.every((campaign) => next.has(campaign.id));

			group.campaigns.forEach((campaign) => {
				if (fullySelected) {
					next.delete(campaign.id);
				} else {
					next.add(campaign.id);
				}
			});

			return next;
		});
	};

	const toggleExpand = (id: number) => {
		setExpandedListIds((prev) => {
			const next = new Set(prev);

			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}

			return next;
		});
	};

	const handleSave = () => {
		handleSelect(campaigns.filter((campaign) => selectedIds.has(campaign.id)));
	};

	const renderBody = () => {
		if (isFetching) {
			return (
				<div className="flex items-center justify-center py-10">
					<Spinner color="#4d66f5" />
				</div>
			);
		}

		if (!visibleGroups.length) {
			return (
				<p className="py-6 px-3 text-sm italic text-gray-700 text-center">
					{isSearching
						? t("copyCampaigns.noSearchResults", { query: searchValue })
						: t("copyCampaigns.noResults")}
				</p>
			);
		}

		return visibleGroups.map((group) => (
			<ListGroupRow
				key={group.listId}
				group={group}
				isExpanded={isSearching || expandedListIds.has(group.listId)}
				isFullySelected={isGroupFullySelected(group, selectedIds)}
				isPartiallySelected={isGroupPartiallySelected(group, selectedIds)}
				selectedIds={selectedIds}
				onToggleCampaign={toggleCampaign}
				onToggleExpand={toggleExpand}
				onToggleList={toggleList}
			/>
		));
	};

	return (
		<Modal
			disableSave={selectedIds.size === 0}
			handleClose={handleClose}
			handleSave={handleSave}
			isOpened={isOpened}
			size="sm"
			submitButtonText={t("copyCampaigns.submit")}
			title={t("copyCampaigns.title")}
		>
			<div className="flex flex-col gap-3">
				<SearchField
					handleChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
					handleReset={() => setSearchValue("")}
					icon={<Icon icon={faSearch} />}
					iconPosition="left"
					name="copy-campaigns-search"
					placeholder={t("copyCampaigns.searchPlaceholder")}
					value={searchValue}
				/>
				<div className="border border-gray-200 rounded-md max-h-[400px] overflow-y-auto bb-scrollbar-darker">
					{renderBody()}
				</div>
			</div>
		</Modal>
	);
};
